import { defineNuxtPlugin, useRuntimeConfig, useRoute, useRouter } from '#app'
import { resolveReferralTtpix } from '@/utils/referral'

/**
 * TikTok Pixel SDK Plugin
 *
 * 對齊 FB Pixel 的動態載入做法，支援以下 pixel 來源並存（同 FB Pixel：兩者皆送）：
 *  1. URL ?ttpix=<pixelId> 動態載入（由 00.query.client.js 雙寫 localStorage ＋ cookie，7 天）
 *  2. 路由級別多 pixel（tiktokPixel.pixels[].routes[] → getMatchingPixel）
 *  3. GTM dataLayer（tiktok_pixel_id）
 *  4. 環境變數 tiktokPixel.pixelId
 *
 * 解析優先序：URL ttpix > 三層解析（網址 → localStorage → cookie）> 路由匹配 > GTM > env。
 * 真正的 ttclid（TikTok 點擊 ID，等同 FB 的 fbclid）由 TikTok pixel SDK 載入後
 * 自行讀 URL 寫 _ttp 做歸因，前端不另外處理。
 */

// TikTok pixel code 僅允許英數，組裝 SDK script src 前先淨化，避免異常值注入
const sanitizePixelId = (id) => {
  if (typeof id !== 'string') return ''
  const trimmed = id.trim()
  return /^[A-Za-z0-9]{1,40}$/.test(trimmed) ? trimmed : ''
}

// 輕量 glob 比對（* 對應單層、** 對應任意），避免額外引入 minimatch 依賴
const matchRoute = (pattern, path) => {
  if (typeof pattern !== 'string') return false
  const specials = '.+^${}()|[]\\?'
  let re = '^'
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i]
    if (ch === '*') {
      if (pattern[i + 1] === '*') {
        re += '.*'
        i++
      } else {
        re += '[^/]*'
      }
    } else if (specials.includes(ch)) {
      re += '\\' + ch
    } else {
      re += ch
    }
  }
  re += '$'
  try {
    return new RegExp(re).test(path)
  } catch (e) {
    return false
  }
}

// 依當前路由找出對應的 pixel 設定（mirror FB facebook-pixel plugin getMatchingPixel）
function getMatchingPixel(options, path) {
  if (!options || !Array.isArray(options.pixels)) return null
  return options.pixels.find((pixel) => pixel && Array.isArray(pixel.routes) && pixel.routes.some((route) => matchRoute(route, path)))
}

/**
 * @class TikTok
 */
class TikTok {
  constructor(options) {
    this.eventsQueue = []
    this.ttqReady = false // 改用 ready 狀態標記
    this.options = options
    this.isEnabled = !options.disabled
    this.loadedPixels = new Set() // 已 ttq.load 的 pixel，去重並支援多 pixel 並存
  }

  /**
   * 標記 SDK 已連動，並開始發送暫存隊列
   */
  setTtqReady() {
    this.ttqReady = true
    this.send()
  }

  /**
   * 內部扁平化工具：將深層物件轉為一層，並過濾掉空值
   */
  flatten(data, prefix = '') {
    const result = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key]
        const newKey = prefix ? `${prefix}_${key}` : key

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          Object.assign(result, this.flatten(value, newKey))
        } else if (value !== '' && value !== null && value !== undefined) {
          result[newKey] = value
        }
      }
    }
    return result
  }

  init(pixelId = null) {
    this.loadPixel(pixelId || this.options.pixelId)
  }

  /**
   * 載入單一 pixel（去重）：同一 pixel 只 load 一次；多個不同 pixel 可同時並存，
   * ttq.track 會廣播給所有已 load 的 pixel（GTM pixel 與 ttpix pixel 兩者皆送）。
   */
  loadPixel(pixelId) {
    const clean = sanitizePixelId(pixelId)
    if (!clean || this.loadedPixels.has(clean)) return false
    this.loadedPixels.add(clean)
    this.options.pixelId = clean // 回寫最近一次載入的 pixel
    this.query('load', clean)
    this.query('page')
    return true // 本次實際載入新 pixel（已送過 page），呼叫端據此避免重複補 pageview
  }

  track(event = null, parameters = null) {
    if (!event) {
      event = this.options.track
    }
    this.query('track', event, parameters)
  }

  /**
   * 將指令推入隊列
   */
  query(cmd, eventOrPixelId, parameters = null) {
    if (!this.isEnabled) return

    // 處理參數扁平化
    let processedParameters = parameters
    if (cmd === 'track' && parameters && typeof parameters === 'object') {
      processedParameters = this.flatten(this.processParameters(eventOrPixelId, parameters))
    }

    this.eventsQueue.push({
      cmd,
      eventOrPixelId,
      parameters: processedParameters,
    })

    this.send()
  }

  /**
   * 針對 TikTok 官方事件的參數補強
   */
  processParameters(eventName, parameters) {
    if (!parameters) return null
    const processed = { ...parameters }

    // 特定事件傳入特定參數
    if (eventName === 'CompletePayment') {
      return {
        content_id: 'Digital',
        content_type: 'product',
        event_id: processed.transaction_id || '',
        value: processed.value || processed.app_value || 0,
        currency: processed.currency || '',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'CompleteRegistration') {
      return {
        content_type: 'registration',
        content_name: 'registration',
        event_id: processed.uidSite || '',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'InitiateCheckout') {
      return {
        content_id: 'Digital',
        content_type: 'product',
        ...processed,
      }
    }
    // 特定事件傳入特定參數
    if (eventName === 'ViewContent') {
      return {
        content_id: 'Digital',
        ...processed,
      }
    }
    return processed
  }

  /**
   * 核心發送邏輯：直接操作 window.ttq
   */
  send() {
    // 確保在瀏覽器端且 window.ttq 已存在
    if (typeof window === 'undefined' || !window.ttq) return

    while (this.eventsQueue.length) {
      const event = this.eventsQueue.shift()

      try {
        // 直接使用全域 window.ttq，它是陣列或函式都能正確接收指令
        if (event.parameters) {
          window.ttq.track(event.eventOrPixelId, event.parameters)
        } else if (event.cmd === 'track') {
          window.ttq.track(event.eventOrPixelId)
        } else {
          // 處理 load 或 page 指令
          window.ttq[event.cmd](event.eventOrPixelId)
        }
      } catch (e) {
        console.error('[TikTok Pixel] Error executing window.ttq:', e)
      }
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const router = useRouter()

  const tiktokConfig = config.public.tiktokPixel || {}
  const parsedOptions = {
    pixelId: null, // 將在初始化時動態獲取
    pixels: Array.isArray(tiktokConfig.pixels) ? tiktokConfig.pixels : [],
    track: tiktokConfig.track || 'PageView',
    disabled: !!tiktokConfig.disabled,
    debug: !!tiktokConfig.debug,
    envPixelId: tiktokConfig.pixelId || null, // env 最後 fallback
  }

  const instance = new TikTok(parsedOptions)

  // 從 GTM dataLayer 取 TikTok pixel id
  const getGtmPixelId = () => {
    if (typeof window === 'undefined' || !window.dataLayer) return ''
    const item = window.dataLayer.find((i) => i && (i.tiktok_pixel_id || i.tiktokPixelId || i.TIKTOK_PIXEL_ID))
    return item ? item.tiktok_pixel_id || item.tiktokPixelId || item.TIKTOK_PIXEL_ID : ''
  }

  // pixel id 解析優先序：URL ttpix > 三層解析(網址 → localStorage → cookie) > 路由匹配 > GTM > env
  const getPixelId = (path) => {
    const fromUrl = sanitizePixelId(route?.query?.ttpix) || sanitizePixelId(resolveReferralTtpix())
    if (fromUrl) return fromUrl
    const matched = getMatchingPixel(parsedOptions, path || '/')
    if (matched && matched.pixelId) return matched.pixelId
    const fromGtm = getGtmPixelId()
    if (fromGtm) return fromGtm
    return parsedOptions.envPixelId || ''
  }

  if (typeof window !== 'undefined') {
    const currentPath = route?.path || window.location.pathname

    // 檢查 SDK 是否已載入（例如 GTM 載入過），避免重複注入
    if (window.ttq) {
      instance.setTtqReady()
      instance.init(getPixelId(currentPath))
    } else {
      // 標準載入腳本
      !(function (w, d, t) {
        w.TiktokAnalyticsObject = t
        var ttq = (w[t] = w[t] || [])
        ttq.methods = ['page', 'track', 'load', 'identify', 'getInstance']
        ttq.setAndDefer = function (t, e) {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
          }
        }
        for (var i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i])
        }
        ttq.instance = function (t) {
          return ttq._i[t] || ttq
        }
        ttq.load = function (e, n) {
          var o = 'https://analytics.tiktok.com/i18n/pixel/events.js'
          ttq._i = ttq._i || {}
          ttq._i[e] = []
          ttq._i[e]._u = o
          ttq._t = ttq._t || {}
          ttq._t[e] = +new Date()
          ttq._o = ttq._o || {}
          ttq._o[e] = n || {}
          var c = d.createElement('script')
          c.type = 'text/javascript'
          c.async = !0
          c.src = o + '?sdkid=' + e + '&lib=' + t
          var a = d.getElementsByTagName('script')[0]
          a.parentNode.insertBefore(c, a)
        }

        // 初始化 instance
        instance.setTtqReady()
        instance.init(getPixelId(currentPath))
      })(window, document, 'ttq')
    }

    // 監聽 GTM 晚到的 TikTok pixel：載入並與既有 pixel 並存（兩者皆送）
    // 加旗標避免 HMR / 其他 plugin 重複包裝 push 造成層層疊加
    if (window.dataLayer && !window.dataLayer._ttpixPatched) {
      const originalPush = window.dataLayer.push
      window.dataLayer.push = function (...args) {
        const result = originalPush.apply(this, args)
        args.forEach((item) => {
          if (item && (item.tiktok_pixel_id || item.tiktokPixelId || item.TIKTOK_PIXEL_ID)) {
            instance.loadPixel(item.tiktok_pixel_id || item.tiktokPixelId || item.TIKTOK_PIXEL_ID)
          }
        })
        return result
      }
      window.dataLayer._ttpixPatched = true
    }

    // SPA 路由切換：依路由切換/載入對應 pixel + 補原生 pageview
    // （mirror FB getMatchingPixel + autoPageView；首次導航的首屏 pageview 已由 init() 送出，跳過避免雙倍）
    let lastPath = ''
    let isFirstNavigation = true
    router.afterEach((to) => {
      const path = to.fullPath || to.path || '/'
      if (path === lastPath) return
      lastPath = path
      const matched = getMatchingPixel(parsedOptions, to.path || path)
      // loadPixel 回傳 true 代表本次剛載入新 pixel（內部已送過 page），後面就不再重複補
      const loadedNewPixel = matched && matched.pixelId ? instance.loadPixel(matched.pixelId) : false
      if (isFirstNavigation) {
        isFirstNavigation = false
        return
      }
      // 僅在已有實際載入的 pixel 時補 pageview，避免無 pixel 站台把 page 指令無上限堆進 stub 佇列
      if (instance.loadedPixels.size > 0 && !loadedNewPixel && typeof window.ttq?.page === 'function') {
        instance.query('page')
      }
    })
  }

  return {
    provide: {
      tiktok: instance,
    },
  }
})
